import { AccountGroup } from '@galaxy/account-group';
import { User } from '../../core/user';
import { AccountGroupPermissionsActions, ActionTypes } from './account-group-permissions.actions';

export interface State {
  search: string;
  pageIndex: number;
  pageSize: number;
  loadingUser: boolean;
  loadingAccountGroups: boolean;
  user: User | null;
  userId: string;
  associatedAccountGroups: AccountGroup[] | null;
  cursor: string;
  hasMore: boolean;
  totalResults: number;
  removingAssociations: string[];
  removingAssociationsErrors: string[];
  error: any;
  selectedAccountGroupIds: string[];
}

export const initialState: State = {
  search: '',
  pageIndex: 0,
  pageSize: 10,
  loadingUser: true,
  loadingAccountGroups: false,
  user: null,
  userId: null,
  associatedAccountGroups: null,
  cursor: null,
  hasMore: false,
  totalResults: 0,
  removingAssociations: [],
  removingAssociationsErrors: [],
  error: null,
  selectedAccountGroupIds: [],
};

export function accountGroupPermissionsReducer(
  state: State = initialState,
  action: AccountGroupPermissionsActions,
): State {
  const nextState = action.type === ActionTypes.INIT ? { ...initialState } : { ...state };

  switch (action.type) {
    case ActionTypes.LOAD_USER:
      nextState.user = null;
      nextState.userId = action.payload.userId;
      nextState.loadingUser = true;
      break;
    case ActionTypes.LOAD_USER_SUCCESS:
      nextState.user = action.payload.user;
      nextState.loadingUser = false;
      nextState.loadingAccountGroups = true;
      break;
    case ActionTypes.LOAD_USER_ERROR:
      nextState.error = action.payload.error;
      break;
    case ActionTypes.LOAD_ACCOUNT_GROUPS:
      nextState.loadingAccountGroups = true;
      nextState.associatedAccountGroups = null;
      nextState.cursor = null;
      break;
    case ActionTypes.LOAD_MORE_ACCOUNT_GROUPS:
      nextState.loadingAccountGroups = true;
      break;
    case ActionTypes.LOAD_ACCOUNT_GROUPS_SUCCESS:
      nextState.loadingAccountGroups = false;
      if (nextState.associatedAccountGroups) {
        nextState.associatedAccountGroups = nextState.associatedAccountGroups.slice();
        nextState.associatedAccountGroups.push(...action.payload.accountGroups);
      } else {
        nextState.associatedAccountGroups = action.payload.accountGroups;
      }
      nextState.cursor = action.payload.cursor;
      nextState.hasMore = action.payload.hasMore;
      nextState.totalResults = action.payload.totalResults;
      break;
    case ActionTypes.LOAD_ACCOUNT_GROUPS_ERROR:
      nextState.error = action.payload.error;
      break;
    case ActionTypes.SEARCH:
      nextState.search = action.payload.searchTerm;
      nextState.pageIndex = 0;
      nextState.associatedAccountGroups = null;
      break;
    case ActionTypes.UPDATE_PRODUCT_PERMISSIONS:
      if (!nextState?.user?.accountAccessPermissions) break;
      nextState.user.accountAccessPermissions[action.payload.accountGroupId] = action.payload.permissions;
      break;
    case ActionTypes.UPDATE_TAB_PERMISSIONS:
      if (!nextState?.user?.businessCenterTabs) break;
      nextState.user.businessCenterTabs[action.payload.accountGroupId] = action.payload.tabIds;
      break;
    case ActionTypes.UPDATE_PAGINATION:
      if (action.payload.pageSize !== nextState.pageSize) {
        nextState.pageSize = action.payload.pageSize;
        nextState.pageIndex = 0;
        nextState.associatedAccountGroups = null;
      } else if (action.payload.pageIndex !== nextState.pageIndex) {
        nextState.pageIndex = action.payload.pageIndex;
      }
      break;
    case ActionTypes.REMOVE_ASSOCIATION:
      nextState.removingAssociations = nextState.removingAssociations.slice();
      nextState.removingAssociations.push(action.payload.accountGroupId);
      nextState.removingAssociationsErrors.splice(
        nextState.removingAssociationsErrors.indexOf(action.payload.accountGroupId),
        1,
      );
      break;
    case ActionTypes.REMOVE_SELECTED_ASSOCIATIONS:
      nextState.removingAssociations = nextState.removingAssociations.slice();
      nextState.removingAssociations.push(...nextState.selectedAccountGroupIds);
      nextState.selectedAccountGroupIds.forEach((accountGroupId) => {
        nextState.removingAssociationsErrors.splice(nextState.removingAssociationsErrors.indexOf(accountGroupId), 1);
      });
      break;
    case ActionTypes.REMOVE_ASSOCIATION_SUCCESS:
      nextState.removingAssociations = nextState.removingAssociations.slice();
      nextState.selectedAccountGroupIds = nextState.selectedAccountGroupIds.slice();
      nextState.associatedAccountGroups = nextState.associatedAccountGroups.slice();
      nextState.removingAssociations.splice(nextState.removingAssociations.indexOf(action.payload.accountGroupId), 1);
      nextState.selectedAccountGroupIds.splice(
        nextState.selectedAccountGroupIds.indexOf(action.payload.accountGroupId),
        1,
      );
      nextState.user.accountGroupAssociations.splice(
        nextState.user.accountGroupAssociations.indexOf(action.payload.accountGroupId),
        1,
      );
      nextState.associatedAccountGroups.splice(
        nextState.associatedAccountGroups.findIndex((ag) => ag.accountGroupId === action.payload.accountGroupId),
        1,
      );
      break;
    case ActionTypes.REMOVE_ASSOCIATION_ERROR:
      nextState.removingAssociations = nextState.removingAssociations.slice();
      nextState.removingAssociations.splice(nextState.removingAssociations.indexOf(action.payload.accountGroupId), 1);
      nextState.removingAssociationsErrors.push(action.payload.accountGroupId);
      break;
    case ActionTypes.TOGGLE_ALL:
      if (nextState.associatedAccountGroups === null) {
        break;
      }
      if (nextState.selectedAccountGroupIds.length < nextState.associatedAccountGroups.length) {
        nextState.selectedAccountGroupIds = nextState.associatedAccountGroups.map((ag) => ag.accountGroupId);
      } else {
        nextState.selectedAccountGroupIds = [];
      }
      break;
    case ActionTypes.TOGGLE_ROW:
      nextState.selectedAccountGroupIds = nextState.selectedAccountGroupIds.slice();
      if (nextState.selectedAccountGroupIds.indexOf(action.payload.accountGroupId) < 0) {
        nextState.selectedAccountGroupIds.push(action.payload.accountGroupId);
      } else {
        nextState.selectedAccountGroupIds.splice(
          nextState.selectedAccountGroupIds.indexOf(action.payload.accountGroupId),
          1,
        );
      }
      break;
  }
  return nextState;
}
